// GameCenterEstadisticas.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import { getPlayerAvatarUrl } from './utils/playerUtils';
import { formatGameDate as formatDate, getSportSeason, getCurrentSeason } from './utils/timeUtils';
import ShotChartVisualization from './components/ShotChartVisualization';
import { computeAdvancedStats } from './utils/statsCalculator';

import {
  Box,
  Typography,
  Avatar,
  useMediaQuery,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  Chip,
  Collapse,
  Alert,
  MenuItem,
  CircularProgress,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VideocamIcon from '@mui/icons-material/Videocam';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ReactPlayer from 'react-player';

import { useStatsTheme } from './v2/theme/statsTheme';
import {
  CanteraCard,
  CanteraDialog,
  CanteraButton,
  CanteraTextField,
} from './components/shared';

const GameCenterEstadisticas = ({ groupeId }) => {
  const { t } = useTranslation();
  const theme = useStatsTheme();
  const [gamesData, setGamesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [periodFields, setPeriodFields] = useState([]);
  const [isLiveGame, setIsLiveGame] = useState(false);
  const [gameNotes, setGameNotes] = useState('');

  const [gameLabel, setGameLabel] = useState('');
  const [adversaryName, setAdversaryName] = useState('');
  const [gameScore, setGameScore] = useState('');
  const [gameLocation, setGameLocation] = useState('domicile');
  const [gameDate, setGameDate] = useState('');

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);

  const [globalTeamAverages, setGlobalTeamAverages] = useState(null); // eslint-disable-line no-unused-vars
  const [globalPlayersAverages, setGlobalPlayersAverages] = useState([]); // eslint-disable-line no-unused-vars

  const [expandedSeasons, setExpandedSeasons] = useState(new Set());
  const [expandedGames, setExpandedGames] = useState(new Set());
  const [expandedAveragesPerSeason, setExpandedAveragesPerSeason] = useState(new Set());

  const [mainTab, setMainTab] = useState(0);
  const [lineups, setLineups] = useState([]);
  const [lineupsLoading, setLineupsLoading] = useState(false);
  const [playerNamesMap, setPlayerNamesMap] = useState({});

  const fetchGamesWithStats = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stats/games-with-stats/${groupeId}`);
      setGamesData(response.data);
      computeGlobalStats(response.data);
    } catch (error) {
      console.error('Error al recuperar las estadísticas de los partidos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupeId) fetchGamesWithStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupeId]);

  useEffect(() => {
    if (gamesData.length > 0 && expandedSeasons.size === 0) {
      setExpandedSeasons(new Set([getCurrentSeason()]));
    }
  }, [gamesData, expandedSeasons]);

  useEffect(() => {
    if (mainTab === 1 && lineups.length === 0 && groupeId) {
      setLineupsLoading(true);
      api.get(`/api/stats/lineups/group/${groupeId}`)
        .then(res => {
          setLineups(res.data.lineups || []);
          const names = {};
          gamesData.forEach(g => { (g.players || []).forEach(p => { names[p.player_id] = p.nom; }); });
          setPlayerNamesMap(names);
        })
        .catch(err => console.error('Error loading lineups:', err))
        .finally(() => setLineupsLoading(false));
    }
  }, [mainTab, groupeId, gamesData, lineups.length]);

  const computeGlobalStats = (data) => {
    if (!data || data.length === 0) {
      setGlobalTeamAverages(null);
      setGlobalPlayersAverages([]);
      return;
    }
    const totalMatches = data.length;
    const accumulators = {
      points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0, turnovers: 0, fouls: 0,
      fgm: 0, fga: 0, tpm: 0, tpa: 0, ftm: 0, fta: 0, offReb: 0, defReb: 0, eval: 0,
    };
    data.forEach((item) => {
      const { teamStats } = item;
      accumulators.points += teamStats.points || 0;
      accumulators.rebounds += teamStats.rebounds || 0;
      accumulators.assists += teamStats.assists || 0;
      accumulators.steals += teamStats.steals || 0;
      accumulators.blocks += teamStats.blocks || 0;
      accumulators.turnovers += teamStats.turnovers || 0;
      accumulators.fouls += teamStats.fouls || 0;
      accumulators.fgm += teamStats.field_goals_made || 0;
      accumulators.fga += teamStats.field_goals_attempted || 0;
      accumulators.tpm += teamStats.three_points_made || 0;
      accumulators.tpa += teamStats.three_points_attempted || 0;
      accumulators.ftm += teamStats.free_throws_made || 0;
      accumulators.fta += teamStats.free_throws_attempted || 0;
      accumulators.offReb += teamStats.offensive_rebounds || 0;
      accumulators.defReb += teamStats.defensive_rebounds || 0;
      accumulators.eval += teamStats.evaluation || 0;
    });
    const globalTeam = {
      matchesPlayed: totalMatches,
      points: (accumulators.points / totalMatches).toFixed(1),
      rebounds: (accumulators.rebounds / totalMatches).toFixed(1),
      assists: (accumulators.assists / totalMatches).toFixed(1),
      steals: (accumulators.steals / totalMatches).toFixed(1),
      blocks: (accumulators.blocks / totalMatches).toFixed(1),
      turnovers: (accumulators.turnovers / totalMatches).toFixed(1),
      fouls: (accumulators.fouls / totalMatches).toFixed(1),
      field_goals_made: Math.round(accumulators.fgm / totalMatches),
      field_goals_attempted: Math.round(accumulators.fga / totalMatches),
      three_points_made: Math.round(accumulators.tpm / totalMatches),
      three_points_attempted: Math.round(accumulators.tpa / totalMatches),
      free_throws_made: Math.round(accumulators.ftm / totalMatches),
      free_throws_attempted: Math.round(accumulators.fta / totalMatches),
      offensive_rebounds: (accumulators.offReb / totalMatches).toFixed(1),
      defensive_rebounds: (accumulators.defReb / totalMatches).toFixed(1),
      evaluation: (accumulators.eval / totalMatches).toFixed(1),
    };
    setGlobalTeamAverages(globalTeam);

    const playersMap = {};
    data.forEach((item) => {
      item.players.forEach((p) => {
        if (!playersMap[p.player_id]) {
          playersMap[p.player_id] = {
            player_id: p.player_id, nom: p.nom,
            minutes_played: 0, points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0,
            turnovers: 0, fouls: 0, field_goals_made: 0, field_goals_attempted: 0,
            three_points_made: 0, three_points_attempted: 0, free_throws_made: 0, free_throws_attempted: 0,
            offensive_rebounds: 0, defensive_rebounds: 0, evaluation: 0,
          };
        }
        const pm = playersMap[p.player_id];
        pm.minutes_played += parseFloat(p.minutes_played || 0);
        pm.points += p.points || 0;
        pm.rebounds += p.rebounds || 0;
        pm.assists += p.assists || 0;
        pm.steals += p.steals || 0;
        pm.blocks += p.blocks || 0;
        pm.turnovers += p.turnovers || 0;
        pm.fouls += p.fouls || 0;
        pm.field_goals_made += p.field_goals_made || 0;
        pm.field_goals_attempted += p.field_goals_attempted || 0;
        pm.three_points_made += p.three_points_made || 0;
        pm.three_points_attempted += p.three_points_attempted || 0;
        pm.free_throws_made += p.free_throws_made || 0;
        pm.free_throws_attempted += p.free_throws_attempted || 0;
        pm.offensive_rebounds += p.offensive_rebounds || 0;
        pm.defensive_rebounds += p.defensive_rebounds || 0;
        pm.evaluation += p.evaluation || 0;
      });
    });
    const playersAverages = Object.values(playersMap).map((p) => ({
      player_id: p.player_id, nom: p.nom,
      minutes_played: (p.minutes_played / totalMatches).toFixed(1),
      points: (p.points / totalMatches).toFixed(1),
      rebounds: (p.rebounds / totalMatches).toFixed(1),
      assists: (p.assists / totalMatches).toFixed(1),
      steals: (p.steals / totalMatches).toFixed(1),
      blocks: (p.blocks / totalMatches).toFixed(1),
      turnovers: (p.turnovers / totalMatches).toFixed(1),
      fouls: (p.fouls / totalMatches).toFixed(1),
      field_goals_made: Math.round(p.field_goals_made / totalMatches),
      field_goals_attempted: Math.round(p.field_goals_attempted / totalMatches),
      three_points_made: Math.round(p.three_points_made / totalMatches),
      three_points_attempted: Math.round(p.three_points_attempted / totalMatches),
      free_throws_made: Math.round(p.free_throws_made / totalMatches),
      free_throws_attempted: Math.round(p.free_throws_attempted / totalMatches),
      offensive_rebounds: (p.offensive_rebounds / totalMatches).toFixed(1),
      defensive_rebounds: (p.defensive_rebounds / totalMatches).toFixed(1),
      evaluation: (p.evaluation / totalMatches).toFixed(1),
    }));
    setGlobalPlayersAverages(playersAverages);
  };

  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key: columnKey, direction });
  };

  const getSortedPlayers = (players) => {
    const { key, direction } = sortConfig;
    if (!key) return players;
    return [...players].sort((a, b) => {
      const valA = isNaN(a[key]) ? a[key] : parseFloat(a[key]);
      const valB = isNaN(b[key]) ? b[key] : parseFloat(b[key]);
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Stat chip — petite carte métrique avec couleur sémantique
  const StatChip = ({ label, value, color }) => (
    <Box
      sx={{
        minWidth: 76,
        flex: '1 1 76px',
        maxWidth: 120,
        p: 1,
        textAlign: 'center',
        bgcolor: theme.withAlpha(color, 0.12),
        border: `1px solid ${theme.withAlpha(color, 0.3)}`,
        borderRadius: `${theme.radius.md}px`,
      }}
    >
      <Typography sx={{ color: theme.text.muted, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ color, fontSize: 18, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );

  const renderTeamStats = (teamStats) => {
    const items = [
      { label: t('gameCenterEstadisticas.points'), value: teamStats.points, color: theme.accent },
      { label: '2pts', value: `${teamStats.field_goals_made || 0}/${teamStats.field_goals_attempted || 0}`, color: theme.actionColors.made },
      { label: '3pts', value: `${teamStats.three_points_made || 0}/${teamStats.three_points_attempted || 0}`, color: theme.actionColors.info },
      { label: 'LF', value: `${teamStats.free_throws_made || 0}/${teamStats.free_throws_attempted || 0}`, color: theme.actionColors.warning },
      { label: 'R.Off', value: teamStats.offensive_rebounds || 0, color: theme.actionColors.rebound.off },
      { label: 'R.Def', value: teamStats.defensive_rebounds || 0, color: theme.actionColors.rebound.def },
      { label: 'R.Tot', value: teamStats.rebounds, color: theme.actionColors.neutral },
      { label: t('gameCenterEstadisticas.assists'), value: teamStats.assists, color: theme.actionColors.ballHandling.ast },
      { label: t('gameCenterEstadisticas.steals'), value: teamStats.steals, color: theme.actionColors.ballHandling.stl },
      { label: t('gameCenterEstadisticas.blocks'), value: teamStats.blocks, color: theme.actionColors.defense.blk },
      { label: t('gameCenterEstadisticas.turnovers'), value: teamStats.turnovers, color: theme.actionColors.ballHandling.tov },
      { label: t('gameCenterEstadisticas.fouls'), value: teamStats.fouls, color: theme.actionColors.defense.foul },
      { label: t('gameCenterEstadisticas.evaluation'), value: teamStats.evaluation || 0, color: theme.accentSecondary || theme.accent },
    ];
    return (
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: theme.text.primary, fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', mb: 1 }}>
          {t('gameCenterEstadisticas.teamStats')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {items.map((it, i) => <StatChip key={i} {...it} />)}
        </Box>
      </Box>
    );
  };

  const renderPlayersStats = (players) => {
    if (isMobile) {
      return (
        <Stack spacing={1}>
          {players.map((player) => (
            <Box
              key={player.player_id}
              sx={{
                p: 1.25,
                bgcolor: theme.surfaces.floating,
                border: `1px solid ${theme.surfaces.border}`,
                borderRadius: `${theme.radius.md}px`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.25 }}>
                <Avatar src={getPlayerAvatarUrl(player.player_id)} alt={player.nom} sx={{ width: 40, height: 40, border: `1px solid ${theme.surfaces.border}` }} />
                <Typography sx={{ color: theme.text.primary, fontSize: 14, fontWeight: 700 }}>{player.nom}</Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, color: theme.text.secondary, fontSize: 12 }}>
                <div>{t('gameCenterEstadisticas.minShort')}: <b style={{ color: theme.text.primary }}>{player.minutes_played}</b></div>
                <div>{t('gameCenterEstadisticas.points')}: <b style={{ color: theme.text.primary }}>{player.points}</b></div>
                <div>{t('gameCenterEstadisticas.evalShort')}: <b style={{ color: (parseFloat(player.evaluation) || 0) >= 0 ? theme.actionColors.made : theme.actionColors.missed }}>{player.evaluation || 0}</b></div>
                <div>2pts: {player.field_goals_made || 0}/{player.field_goals_attempted || 0}</div>
                <div>3pts: {player.three_points_made || 0}/{player.three_points_attempted || 0}</div>
                <div>{t('gameCenterEstadisticas.freeThrowsShort')}: {player.free_throws_made || 0}/{player.free_throws_attempted || 0}</div>
                <div>{t('gameCenterEstadisticas.offRebShort')}: {player.offensive_rebounds || 0}</div>
                <div>{t('gameCenterEstadisticas.defRebShort')}: {player.defensive_rebounds || 0}</div>
                <div>{t('gameCenterEstadisticas.totRebShort')}: {player.rebounds}</div>
                <div>{t('gameCenterEstadisticas.assistsShort')}: {player.assists}</div>
                <div>{t('gameCenterEstadisticas.steals')}: {player.steals}</div>
                <div>{t('gameCenterEstadisticas.blocks')}: {player.blocks}</div>
                <div>{t('gameCenterEstadisticas.turnovers')}: {player.turnovers}</div>
                <div>{t('gameCenterEstadisticas.fouls')}: {player.fouls}</div>
              </Box>
            </Box>
          ))}
        </Stack>
      );
    }
    const sortedPlayers = getSortedPlayers(players);
    const columns = [
      { key: 'nom', label: t('gameCenterEstadisticas.player') },
      { key: 'minutes_played', label: t('gameCenterEstadisticas.minShort') },
      { key: 'points', label: t('gameCenterEstadisticas.ptsShort') },
      { key: 'field_goals', label: '2pts' },
      { key: 'three_points', label: '3pts' },
      { key: 'free_throws', label: t('gameCenterEstadisticas.freeThrowsShort') },
      { key: 'offensive_rebounds', label: t('gameCenterEstadisticas.offRebShort') },
      { key: 'defensive_rebounds', label: t('gameCenterEstadisticas.defRebShort') },
      { key: 'rebounds', label: t('gameCenterEstadisticas.totRebShort') },
      { key: 'assists', label: t('gameCenterEstadisticas.assistsTableShort') },
      { key: 'steals', label: t('gameCenterEstadisticas.stealsTableShort') },
      { key: 'blocks', label: t('gameCenterEstadisticas.blocksTableShort') },
      { key: 'turnovers', label: t('gameCenterEstadisticas.turnoversTableShort') },
      { key: 'fouls', label: t('gameCenterEstadisticas.foulsTableShort') },
      { key: 'evaluation', label: t('gameCenterEstadisticas.evalShort') },
    ];
    return (
      <TableContainer
        sx={{
          bgcolor: theme.surfaces.floating,
          border: `1px solid ${theme.surfaces.border}`,
          borderRadius: `${theme.radius.md}px`,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: theme.surfaces.base }}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: '0.04em',
                    color: sortConfig.key === col.key ? theme.accent : theme.text.secondary,
                    borderBottom: `1px solid ${theme.surfaces.border}`,
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { color: theme.accent },
                  }}
                >
                  {col.label}
                  {sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPlayers.map((player) => {
              const evalVal = parseFloat(player.evaluation) || 0;
              return (
                <TableRow
                  key={player.player_id}
                  sx={{
                    '& td': { color: theme.text.primary, borderBottom: `1px solid ${theme.surfaces.divider}`, fontSize: 13 },
                    '&:hover': { bgcolor: theme.surfaces.elevated },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={getPlayerAvatarUrl(player.player_id)} alt={player.nom} sx={{ width: 28, height: 28, border: `1px solid ${theme.surfaces.border}` }} />
                      <span>{player.nom}</span>
                    </Box>
                  </TableCell>
                  <TableCell>{player.minutes_played}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{player.points}</TableCell>
                  <TableCell>{player.field_goals_made || 0}/{player.field_goals_attempted || 0}</TableCell>
                  <TableCell>{player.three_points_made || 0}/{player.three_points_attempted || 0}</TableCell>
                  <TableCell>{player.free_throws_made || 0}/{player.free_throws_attempted || 0}</TableCell>
                  <TableCell>{player.offensive_rebounds || 0}</TableCell>
                  <TableCell>{player.defensive_rebounds || 0}</TableCell>
                  <TableCell>{player.rebounds}</TableCell>
                  <TableCell>{player.assists}</TableCell>
                  <TableCell>{player.steals}</TableCell>
                  <TableCell>{player.blocks}</TableCell>
                  <TableCell>{player.turnovers}</TableCell>
                  <TableCell>{player.fouls}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: evalVal >= 0 ? theme.actionColors.made : theme.actionColors.missed }}>
                    {player.evaluation || 0}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const openDialogForGame = async (game) => {
    setSelectedGame(game);
    setVideoUrl(game.url || '');
    setGameNotes(game.notes || '');
    setGameLabel(game.game_label || '');
    setAdversaryName(game.adversary_name || '');
    setGameScore(game.score || '');
    setGameLocation(game.game_location || 'domicile');

    let dateValue = '';
    if (game.game_date) {
      const dateString = String(game.game_date);
      if (dateString.includes('T')) {
        const dateObj = new Date(dateString);
        const year = dateObj.getUTCFullYear();
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        dateValue = `${year}-${month}-${day}`;
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        dateValue = dateString;
      } else if (game.game_date instanceof Date) {
        const year = game.game_date.getUTCFullYear();
        const month = String(game.game_date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(game.game_date.getUTCDate()).padStart(2, '0');
        dateValue = `${year}-${month}-${day}`;
      }
    }
    setGameDate(dateValue);

    let isLive = false;
    if (!game.url && game.id) {
      try {
        const response = await api.get(`/api/stats/actions/${game.id}`);
        if (response.data && response.data.actions && response.data.actions.length > 0) {
          isLive = response.data.actions.some(a => a.videoTimestamp === null || a.videoTimestamp === undefined);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du type de match:', error);
      }
    }
    setIsLiveGame(isLive);

    const fields = [];
    for (let i = 1; i <= game.number_of_periods; i++) {
      const periodSec = game[`periode${i}`];
      if (periodSec) {
        const h = Math.floor(periodSec / 3600);
        const m = Math.floor((periodSec % 3600) / 60);
        const s = periodSec % 60;
        fields.push(`${h} ${m} ${s}`);
      } else {
        fields.push('0 0 0');
      }
    }
    setPeriodFields(fields);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedGame(null);
    setVideoUrl('');
    setPeriodFields([]);
    setGameNotes('');
    setGameLabel('');
    setAdversaryName('');
    setGameScore('');
    setGameLocation('domicile');
    setGameDate('');
  };

  const convertHmsToSec = (hms) => {
    if (!hms) return 0;
    const [hh, mm, ss] = hms.split(' ').map(Number);
    return hh * 3600 + mm * 60 + ss;
  };

  const handleSave = async () => {
    if (!selectedGame) return;
    try {
      const gameId = selectedGame.id;
      const periodOffsetsInSeconds = periodFields.map(convertHmsToSec);

      if (isLiveGame && videoUrl) {
        await api.post(`/api/stats/sync-video/${gameId}`, {
          videoUrl, periodOffsets: periodOffsetsInSeconds,
        });
        await api.put(`/api/stats/games/${gameId}`, {
          game_label: gameLabel, adversary_name: adversaryName, score: gameScore,
          game_location: gameLocation, game_date: gameDate, notes: gameNotes,
        });
      } else {
        await api.put(`/api/stats/games/${gameId}`, {
          game_label: gameLabel, adversary_name: adversaryName, score: gameScore,
          game_location: gameLocation, game_date: gameDate, url: videoUrl,
          periods: periodFields, notes: gameNotes,
        });
      }

      const newGamesData = gamesData.map((g) => {
        if (g.game.id === gameId) {
          return {
            ...g,
            game: {
              ...g.game,
              game_label: gameLabel, adversary_name: adversaryName, score: gameScore,
              game_location: gameLocation, game_date: gameDate, url: videoUrl, notes: gameNotes,
              ...periodFields.reduce((acc, val, idx) => {
                acc[`periode${idx + 1}`] = convertHmsToSec(val);
                return acc;
              }, {}),
            },
          };
        }
        return g;
      });
      setGamesData(newGamesData);
      computeGlobalStats(newGamesData);
      closeDialog();
      if (isLiveGame && videoUrl) await fetchGamesWithStats();
    } catch (error) {
      console.error('Error al guardar los períodos:', error);
    }
  };

  const openDeleteConfirmation = (game) => {
    setGameToDelete(game);
    setOpenDeleteDialog(true);
  };
  const closeDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setGameToDelete(null);
  };
  const handleDeleteGame = async () => {
    if (!gameToDelete) return;
    try {
      await api.delete(`/api/stats/games/${gameToDelete.id}`);
      const newGamesData = gamesData.filter((g) => g.game.id !== gameToDelete.id);
      setGamesData(newGamesData);
      computeGlobalStats(newGamesData);
      closeDeleteDialog();
    } catch (error) {
      console.error('Error al eliminar el partido:', error);
      alert('Error al eliminar el partido. Por favor, inténtalo de nuevo.');
    }
  };

  const groupGamesBySeason = () => {
    const seasonGroups = {};
    gamesData.forEach((gameItem) => {
      const season = getSportSeason(gameItem.game.game_date);
      if (!seasonGroups[season]) seasonGroups[season] = [];
      seasonGroups[season].push(gameItem);
    });
    const sortedSeasons = Object.keys(seasonGroups).sort((a, b) => {
      const [yearA] = a.split('-').map(Number);
      const [yearB] = b.split('-').map(Number);
      return yearB - yearA;
    });
    return { seasonGroups, sortedSeasons };
  };

  const computeSeasonStats = (seasonGames) => {
    if (!seasonGames || seasonGames.length === 0) {
      return { teamAverages: null, playersAverages: [], wins: 0, losses: 0, winPercentage: 0 };
    }
    let wins = 0, losses = 0;
    seasonGames.forEach((item) => {
      const score = item.game.score;
      if (score && score.includes('-')) {
        const [ourScore, theirScore] = score.split('-').map(s => parseInt(s.trim()));
        if (!isNaN(ourScore) && !isNaN(theirScore)) {
          if (ourScore > theirScore) wins++;
          else if (ourScore < theirScore) losses++;
        }
      }
    });
    const winPercentage = (wins + losses) > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : 0;

    const totalMatches = seasonGames.length;
    const acc = {
      points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0, turnovers: 0, fouls: 0,
      fgm: 0, fga: 0, tpm: 0, tpa: 0, ftm: 0, fta: 0, offReb: 0, defReb: 0, eval: 0,
    };
    seasonGames.forEach((item) => {
      const { teamStats } = item;
      acc.points += teamStats.points || 0;
      acc.rebounds += teamStats.rebounds || 0;
      acc.assists += teamStats.assists || 0;
      acc.steals += teamStats.steals || 0;
      acc.blocks += teamStats.blocks || 0;
      acc.turnovers += teamStats.turnovers || 0;
      acc.fouls += teamStats.fouls || 0;
      acc.fgm += teamStats.field_goals_made || 0;
      acc.fga += teamStats.field_goals_attempted || 0;
      acc.tpm += teamStats.three_points_made || 0;
      acc.tpa += teamStats.three_points_attempted || 0;
      acc.ftm += teamStats.free_throws_made || 0;
      acc.fta += teamStats.free_throws_attempted || 0;
      acc.offReb += teamStats.offensive_rebounds || 0;
      acc.defReb += teamStats.defensive_rebounds || 0;
      acc.eval += teamStats.evaluation || 0;
    });
    const teamAverages = {
      matchesPlayed: totalMatches,
      points: (acc.points / totalMatches).toFixed(1),
      rebounds: (acc.rebounds / totalMatches).toFixed(1),
      assists: (acc.assists / totalMatches).toFixed(1),
      steals: (acc.steals / totalMatches).toFixed(1),
      blocks: (acc.blocks / totalMatches).toFixed(1),
      turnovers: (acc.turnovers / totalMatches).toFixed(1),
      fouls: (acc.fouls / totalMatches).toFixed(1),
      field_goals_made: Math.round(acc.fgm / totalMatches),
      field_goals_attempted: Math.round(acc.fga / totalMatches),
      three_points_made: Math.round(acc.tpm / totalMatches),
      three_points_attempted: Math.round(acc.tpa / totalMatches),
      free_throws_made: Math.round(acc.ftm / totalMatches),
      free_throws_attempted: Math.round(acc.fta / totalMatches),
      offensive_rebounds: (acc.offReb / totalMatches).toFixed(1),
      defensive_rebounds: (acc.defReb / totalMatches).toFixed(1),
      evaluation: (acc.eval / totalMatches).toFixed(1),
    };

    const playersMap = {};
    seasonGames.forEach((item) => {
      item.players.forEach((p) => {
        if (!playersMap[p.player_id]) {
          playersMap[p.player_id] = {
            player_id: p.player_id, nom: p.nom,
            minutes_played: 0, points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0,
            turnovers: 0, fouls: 0, field_goals_made: 0, field_goals_attempted: 0,
            three_points_made: 0, three_points_attempted: 0, free_throws_made: 0, free_throws_attempted: 0,
            offensive_rebounds: 0, defensive_rebounds: 0, evaluation: 0,
          };
        }
        const pm = playersMap[p.player_id];
        pm.minutes_played += parseFloat(p.minutes_played || 0);
        pm.points += p.points || 0;
        pm.rebounds += p.rebounds || 0;
        pm.assists += p.assists || 0;
        pm.steals += p.steals || 0;
        pm.blocks += p.blocks || 0;
        pm.turnovers += p.turnovers || 0;
        pm.fouls += p.fouls || 0;
        pm.field_goals_made += p.field_goals_made || 0;
        pm.field_goals_attempted += p.field_goals_attempted || 0;
        pm.three_points_made += p.three_points_made || 0;
        pm.three_points_attempted += p.three_points_attempted || 0;
        pm.free_throws_made += p.free_throws_made || 0;
        pm.free_throws_attempted += p.free_throws_attempted || 0;
        pm.offensive_rebounds += p.offensive_rebounds || 0;
        pm.defensive_rebounds += p.defensive_rebounds || 0;
        pm.evaluation += p.evaluation || 0;
      });
    });
    const playersAverages = Object.values(playersMap).map((p) => ({
      player_id: p.player_id, nom: p.nom,
      minutes_played: (p.minutes_played / totalMatches).toFixed(1),
      points: (p.points / totalMatches).toFixed(1),
      rebounds: (p.rebounds / totalMatches).toFixed(1),
      assists: (p.assists / totalMatches).toFixed(1),
      steals: (p.steals / totalMatches).toFixed(1),
      blocks: (p.blocks / totalMatches).toFixed(1),
      turnovers: (p.turnovers / totalMatches).toFixed(1),
      fouls: (p.fouls / totalMatches).toFixed(1),
      field_goals_made: Math.round(p.field_goals_made / totalMatches),
      field_goals_attempted: Math.round(p.field_goals_attempted / totalMatches),
      three_points_made: Math.round(p.three_points_made / totalMatches),
      three_points_attempted: Math.round(p.three_points_attempted / totalMatches),
      free_throws_made: Math.round(p.free_throws_made / totalMatches),
      free_throws_attempted: Math.round(p.free_throws_attempted / totalMatches),
      offensive_rebounds: (p.offensive_rebounds / totalMatches).toFixed(1),
      defensive_rebounds: (p.defensive_rebounds / totalMatches).toFixed(1),
      evaluation: (p.evaluation / totalMatches).toFixed(1),
    }));
    return { teamAverages, playersAverages, wins, losses, winPercentage };
  };

  const toggleSeason = (season) => {
    setExpandedSeasons((prev) => {
      const next = new Set(prev);
      if (next.has(season)) next.delete(season); else next.add(season);
      return next;
    });
  };
  const toggleGame = (gameId) => {
    setExpandedGames((prev) => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId); else next.add(gameId);
      return next;
    });
  };
  const toggleSeasonAverages = (season) => {
    setExpandedAveragesPerSeason((prev) => {
      const next = new Set(prev);
      if (next.has(season)) next.delete(season); else next.add(season);
      return next;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, gap: 1.5 }}>
        <CircularProgress size={22} sx={{ color: theme.accent }} />
        <Typography sx={{ color: theme.text.secondary, fontSize: 14, fontWeight: 500 }}>
          {t('gameCenterEstadisticas.loading')}
        </Typography>
      </Box>
    );
  }

  if (!gamesData || gamesData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: theme.text.secondary, fontSize: 14 }}>
          {t('gameCenterEstadisticas.noGamesFound')}
        </Typography>
      </Box>
    );
  }

  const { seasonGroups, sortedSeasons } = groupGamesBySeason();

  return (
    <Box sx={{ mt: 1 }}>
      {/* Main Tabs */}
      <Box sx={{ borderBottom: `1px solid ${theme.surfaces.border}`, mb: 2 }}>
        <Tabs
          value={mainTab}
          onChange={(_, val) => setMainTab(val)}
          TabIndicatorProps={{ sx: { bgcolor: theme.accent, height: 3, borderRadius: '3px 3px 0 0' } }}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44, color: theme.text.secondary, fontWeight: 600, textTransform: 'none',
              fontSize: 14, '&.Mui-selected': { color: theme.accent },
            },
          }}
        >
          <Tab label={t('gameCenterEstadisticas.statsTab', { defaultValue: 'Statistiques' })} />
          <Tab label={t('gameCenterEstadisticas.lineupsTab', { defaultValue: 'Lineups' })} />
        </Tabs>
      </Box>

      {/* Tab 0: Stats par saison */}
      {mainTab === 0 && (
        <Stack spacing={2}>
          {sortedSeasons.map((season) => {
            const seasonGames = seasonGroups[season];
            const { teamAverages, playersAverages, wins, losses, winPercentage } = computeSeasonStats(seasonGames);
            const isSeasonOpen = expandedSeasons.has(season);
            const isAvgOpen = expandedAveragesPerSeason.has(season);

            return (
              <CanteraCard key={season} tone="dark" padding={0}>
                {/* Header saison */}
                <Box
                  onClick={() => toggleSeason(season)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    px: 2, py: 1.5, cursor: 'pointer',
                    '&:hover': { bgcolor: theme.accentSofter },
                    transition: theme.motion.fast,
                  }}
                >
                  <EmojiEventsIcon sx={{ color: theme.accent, fontSize: 22 }} />
                  <Typography sx={{ flex: 1, color: theme.text.primary, fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
                    {t('gameCenterEstadisticas.season')} {season}
                    <Box component="span" sx={{ color: theme.text.muted, fontWeight: 500, ml: 1, fontSize: 14 }}>
                      ({seasonGames.length} {seasonGames.length > 1 ? t('gameCenterEstadisticas.games') : t('gameCenterEstadisticas.game')})
                    </Box>
                  </Typography>
                  <ExpandMoreIcon sx={{ color: theme.text.secondary, transform: isSeasonOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: theme.motion.fast }} />
                </Box>

                <Collapse in={isSeasonOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ borderTop: `1px solid ${theme.surfaces.border}`, p: 2 }}>
                    {/* Win/Loss/% */}
                    {teamAverages && (
                      <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
                          {[
                            { v: wins, label: wins === 1 ? t('gameCenterEstadisticas.victory') : t('gameCenterEstadisticas.victories'), color: theme.actionColors.made },
                            { v: losses, label: losses === 1 ? t('gameCenterEstadisticas.defeat') : t('gameCenterEstadisticas.defeats'), color: theme.actionColors.missed },
                            { v: `${winPercentage}%`, label: t('gameCenterEstadisticas.winPercentage'), color: theme.actionColors.info },
                          ].map((c, i) => (
                            <Box
                              key={i}
                              sx={{
                                p: 1.5, textAlign: 'center',
                                bgcolor: theme.withAlpha(c.color, 0.14),
                                border: `1px solid ${theme.withAlpha(c.color, 0.35)}`,
                                borderRadius: `${theme.radius.lg}px`,
                              }}
                            >
                              <Typography sx={{ color: c.color, fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                                {c.v}
                              </Typography>
                              <Typography sx={{ color: theme.text.secondary, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', mt: 0.5 }}>
                                {c.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        {renderTeamStats(teamAverages)}
                      </>
                    )}

                    {/* Promedios jugadores — sub-section expandable */}
                    {playersAverages && playersAverages.length > 0 && (
                      <Box
                        sx={{
                          mb: 2,
                          bgcolor: theme.surfaces.base,
                          border: `1px solid ${theme.surfaces.border}`,
                          borderRadius: `${theme.radius.md}px`,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          onClick={() => toggleSeasonAverages(season)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 1, cursor: 'pointer',
                            '&:hover': { bgcolor: theme.accentSofter },
                          }}
                        >
                          <Typography sx={{ flex: 1, color: theme.text.primary, fontSize: 14, fontWeight: 700 }}>
                            {t('gameCenterEstadisticas.averagePlayerStats')}
                          </Typography>
                          <ExpandMoreIcon sx={{ color: theme.text.secondary, transform: isAvgOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: theme.motion.fast }} />
                        </Box>
                        <Collapse in={isAvgOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.surfaces.border}` }}>
                            {renderPlayersStats(playersAverages)}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Liste matchs */}
                    <Typography sx={{ color: theme.text.primary, fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', mb: 1.25 }}>
                      {t('gameCenterEstadisticas.matches')}
                    </Typography>
                    <Stack spacing={1}>
                      {seasonGames.map((gameItem) => {
                        const { game, teamStats, players } = gameItem;
                        const groupeName = game.groupe_name || game.game_label;
                        const isGameOpen = expandedGames.has(game.id);

                        let gameResult = null;
                        let leftTeam, rightTeam, leftScore, rightScore;
                        if (game.score && game.score.includes('-')) {
                          const [ourScore, theirScore] = game.score.split('-').map(s => parseInt(s.trim()));
                          if (game.game_location === 'domicile') {
                            leftTeam = groupeName; leftScore = ourScore; rightTeam = game.adversary_name; rightScore = theirScore;
                          } else {
                            leftTeam = game.adversary_name; leftScore = theirScore; rightTeam = groupeName; rightScore = ourScore;
                          }
                          if (!isNaN(ourScore) && !isNaN(theirScore)) {
                            if (ourScore > theirScore) gameResult = t('gameCenterEstadisticas.victoryResult');
                            else if (ourScore < theirScore) gameResult = t('gameCenterEstadisticas.defeatResult');
                            else gameResult = t('gameCenterEstadisticas.drawResult');
                          }
                        } else {
                          if (game.game_location === 'domicile') { leftTeam = groupeName; rightTeam = game.adversary_name; }
                          else { leftTeam = game.adversary_name; rightTeam = groupeName; }
                          leftScore = '-'; rightScore = '-';
                        }

                        const resultColor = gameResult === t('gameCenterEstadisticas.victoryResult') ? theme.actionColors.made
                          : gameResult === t('gameCenterEstadisticas.defeatResult') ? theme.actionColors.missed
                          : theme.actionColors.warning;

                        return (
                          <Box
                            key={game.id}
                            sx={{
                              bgcolor: theme.surfaces.base,
                              border: `1px solid ${theme.surfaces.border}`,
                              borderRadius: `${theme.radius.md}px`,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              onClick={() => toggleGame(game.id)}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 },
                                px: { xs: 1, sm: 1.5 }, py: 1, cursor: 'pointer', flexWrap: { xs: 'wrap', md: 'nowrap' },
                                '&:hover': { bgcolor: theme.accentSofter },
                                transition: theme.motion.fast,
                              }}
                            >
                              {game.game_label && (
                                <Typography sx={{ color: theme.text.muted, fontSize: 10, minWidth: 70, display: { xs: 'none', md: 'block' } }}>
                                  {game.game_label}
                                </Typography>
                              )}
                              <Typography sx={{ flex: 1, textAlign: 'right', color: theme.text.primary, fontSize: 13, fontWeight: 600 }}>
                                {leftTeam}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                  bgcolor: theme.accentSoft, border: `1px solid ${theme.accentBorder}`,
                                  px: 1.25, py: 0.25, borderRadius: `${theme.radius.sm}px`,
                                  minWidth: 76, justifyContent: 'center',
                                }}
                              >
                                <Typography sx={{ color: theme.accent, fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                  {leftScore}
                                </Typography>
                                <Typography sx={{ color: theme.accent, fontSize: 12, fontWeight: 700 }}>-</Typography>
                                <Typography sx={{ color: theme.accent, fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                  {rightScore}
                                </Typography>
                              </Box>
                              <Typography sx={{ flex: 1, textAlign: 'left', color: theme.text.primary, fontSize: 13, fontWeight: 600 }}>
                                {rightTeam}
                              </Typography>
                              {gameResult && (
                                <Box
                                  sx={{
                                    bgcolor: theme.withAlpha(resultColor, 0.16),
                                    color: resultColor,
                                    border: `1px solid ${theme.withAlpha(resultColor, 0.4)}`,
                                    px: 1, py: 0.25, borderRadius: `${theme.radius.sm}px`,
                                    fontWeight: 700, fontSize: 10, minWidth: 58, textAlign: 'center',
                                    textTransform: 'uppercase', letterSpacing: '0.04em',
                                  }}
                                >
                                  {gameResult}
                                </Box>
                              )}
                              <Typography sx={{ color: theme.text.muted, fontSize: 10, minWidth: 100, display: { xs: 'none', sm: 'block' } }}>
                                {formatDate(game.game_date)} · {game.game_location === 'domicile' ? t('gameCenterEstadisticas.homeShort') : t('gameCenterEstadisticas.awayShort')}
                              </Typography>
                              <ExpandMoreIcon sx={{ color: theme.text.secondary, transform: isGameOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: theme.motion.fast, fontSize: 20 }} />
                            </Box>

                            <Collapse in={isGameOpen} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, borderTop: `1px solid ${theme.surfaces.border}` }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1.5 }}>
                                  <CanteraButton variant="danger" size="sm" startIcon={<DeleteIcon fontSize="small" />} onClick={() => openDeleteConfirmation(game)}>
                                    {t('gameCenterEstadisticas.delete')}
                                  </CanteraButton>
                                  <CanteraButton variant="primary" size="sm" startIcon={<EditIcon fontSize="small" />} onClick={() => openDialogForGame(game)}>
                                    {t('gameCenterEstadisticas.modify')}
                                  </CanteraButton>
                                </Box>

                                {game.url && (
                                  <Box sx={{ mb: 2, borderRadius: `${theme.radius.md}px`, overflow: 'hidden', border: `1px solid ${theme.surfaces.border}` }}>
                                    <ReactPlayer url={game.url} controls width="100%" />
                                  </Box>
                                )}

                                {game.notes && (
                                  <Box
                                    sx={{
                                      p: 1.5, mb: 2,
                                      bgcolor: theme.withAlpha(theme.actionColors.warning, 0.1),
                                      border: `1px solid ${theme.withAlpha(theme.actionColors.warning, 0.3)}`,
                                      borderRadius: `${theme.radius.md}px`,
                                    }}
                                  >
                                    <Typography sx={{ color: theme.actionColors.warning, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
                                      {t('gameCenterEstadisticas.notes')}
                                    </Typography>
                                    <Typography sx={{ color: theme.text.primary, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                                      {game.notes}
                                    </Typography>
                                  </Box>
                                )}

                                {renderTeamStats(teamStats)}

                                <Box sx={{ mt: 2 }}>
                                  <Typography sx={{ color: theme.text.primary, fontSize: 14, fontWeight: 700, mb: 1 }}>
                                    {t('gameCenterEstadisticas.playerStats')}
                                  </Typography>
                                  {renderPlayersStats(players)}
                                </Box>

                                {(() => {
                                  const adv = computeAdvancedStats(teamStats);
                                  if (!adv || !adv.ts_pct) return null;
                                  const advStats = [
                                    { label: 'TS%', value: adv.ts_pct },
                                    { label: 'eFG%', value: adv.efg_pct },
                                    { label: 'FG%', value: adv.fg_pct },
                                    { label: '2PT%', value: adv.two_pt_pct },
                                    { label: '3PT%', value: adv.three_pt_pct },
                                    { label: 'FT%', value: adv.ft_pct },
                                    { label: 'AST/TO', value: adv.ast_to_ratio },
                                    { label: 'PTS/Poss', value: adv.pts_per_possession },
                                  ].filter(s => s.value !== null);
                                  return (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: theme.surfaces.floating, border: `1px solid ${theme.surfaces.border}`, borderRadius: `${theme.radius.md}px` }}>
                                      <Typography sx={{ color: theme.text.primary, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 1 }}>
                                        {t('gameCenterEstadisticas.advancedStats', { defaultValue: 'Stats avancées' })}
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                        {advStats.map(s => (
                                          <Box key={s.label} sx={{ textAlign: 'center', minWidth: 64 }}>
                                            <Typography sx={{ color: theme.accent, fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                              {s.value}
                                            </Typography>
                                            <Typography sx={{ color: theme.text.muted, fontSize: 10, fontWeight: 600, letterSpacing: '0.04em' }}>
                                              {s.label}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  );
                                })()}

                                <Box sx={{ mt: 2 }}>
                                  <Typography sx={{ color: theme.text.primary, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 1 }}>
                                    {t('gameCenterEstadisticas.shotChart', { defaultValue: 'Shot Chart' })}
                                  </Typography>
                                  <ShotChartVisualization gameId={game.id} />
                                </Box>
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                </Collapse>
              </CanteraCard>
            );
          })}
        </Stack>
      )}

      {/* Tab 1: Lineups */}
      {mainTab === 1 && (
        <Box>
          {lineupsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: theme.accent }} />
            </Box>
          ) : lineups.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: theme.text.muted, fontSize: 13 }}>
                {t('gameCenterEstadisticas.noLineups', { defaultValue: 'Pas de données de lineups disponibles' })}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{
                bgcolor: theme.surfaces.floating,
                border: `1px solid ${theme.surfaces.border}`,
                borderRadius: `${theme.radius.md}px`,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.surfaces.base }}>
                    {[
                      t('gameCenterEstadisticas.lineup', { defaultValue: 'Lineup' }),
                      t('gameCenterEstadisticas.players', { defaultValue: 'Joueurs' }),
                      t('gameCenterEstadisticas.minutes', { defaultValue: 'Minutes' }),
                      '+/-',
                    ].map((label, i) => (
                      <TableCell
                        key={i}
                        align={i > 0 ? 'center' : 'left'}
                        sx={{ color: theme.text.secondary, fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', borderBottom: `1px solid ${theme.surfaces.border}` }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineups.map((lineup, idx) => {
                    const pm = lineup.plusMinus;
                    const pmColor = pm > 0 ? theme.actionColors.made : pm < 0 ? theme.actionColors.missed : theme.text.secondary;
                    return (
                      <TableRow
                        key={idx}
                        sx={{ '& td': { color: theme.text.primary, borderBottom: `1px solid ${theme.surfaces.divider}`, fontSize: 13 }, '&:hover': { bgcolor: theme.surfaces.elevated } }}
                      >
                        <TableCell>#{idx + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {lineup.playerIds.map(pid => (
                              <Chip
                                key={pid}
                                label={playerNamesMap[pid] || `#${pid}`}
                                size="small"
                                sx={{
                                  height: 22, fontSize: 11, fontWeight: 600,
                                  bgcolor: theme.accentSoft, color: theme.accent,
                                  border: `1px solid ${theme.accentBorder}`,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{lineup.minutes}</TableCell>
                        <TableCell align="center" sx={{ color: `${pmColor} !important`, fontWeight: 700 }}>
                          {pm > 0 ? '+' : ''}{pm}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Dialog edit game */}
      <CanteraDialog
        open={openDialog}
        onClose={closeDialog}
        maxWidth="md"
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <VideocamIcon sx={{ color: theme.accent, fontSize: 20 }} />
            <span>{isLiveGame ? t('gameCenterEstadisticas.syncLiveVideo') : t('gameCenterEstadisticas.modifyGame')}</span>
          </Stack>
        }
        actions={
          <>
            <CanteraButton variant="ghost" onClick={closeDialog}>{t('gameCenterEstadisticas.cancel')}</CanteraButton>
            <CanteraButton variant="primary" onClick={handleSave}>{t('gameCenterEstadisticas.save')}</CanteraButton>
          </>
        }
      >
        <Typography sx={{ color: theme.text.muted, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5 }}>
          {t('gameCenterEstadisticas.gameInfo')}
        </Typography>

        <Stack spacing={2}>
          <CanteraTextField
            label={t('gameCenterEstadisticas.gameTitle')} fullWidth
            value={gameLabel} onChange={(e) => setGameLabel(e.target.value)}
            placeholder={t('gameCenterEstadisticas.gameTitlePlaceholder')}
          />
          <CanteraTextField
            label={t('gameCenterEstadisticas.adversary')} fullWidth
            value={adversaryName} onChange={(e) => setAdversaryName(e.target.value)}
            placeholder={t('gameCenterEstadisticas.adversaryPlaceholder')}
          />
          <CanteraTextField
            label={t('gameCenterEstadisticas.score')} fullWidth
            value={gameScore} onChange={(e) => setGameScore(e.target.value)}
            placeholder={t('gameCenterEstadisticas.scorePlaceholder')}
            helperText={t('gameCenterEstadisticas.scoreHelperText')}
          />
          <CanteraTextField
            select label={t('gameCenterEstadisticas.location')} fullWidth
            value={gameLocation} onChange={(e) => setGameLocation(e.target.value)}
          >
            <MenuItem value="domicile">{t('gameCenterEstadisticas.home')}</MenuItem>
            <MenuItem value="exterieur">{t('gameCenterEstadisticas.away')}</MenuItem>
          </CanteraTextField>
          <CanteraTextField
            label={t('gameCenterEstadisticas.gameDate')} type="date" fullWidth
            value={gameDate} onChange={(e) => setGameDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <Typography sx={{ color: theme.text.muted, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5, mt: 3 }}>
          {t('gameCenterEstadisticas.gameVideo')}
        </Typography>

        <Stack spacing={2}>
          <CanteraTextField
            label={t('gameCenterEstadisticas.videoUrl')} fullWidth
            value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=..."
          />
          <CanteraTextField
            label={t('gameCenterEstadisticas.gameNotes')} fullWidth multiline rows={4}
            value={gameNotes} onChange={(e) => setGameNotes(e.target.value)}
            placeholder={t('gameCenterEstadisticas.gameNotesPlaceholder')}
          />
        </Stack>

        {videoUrl && (
          <Box sx={{ mt: 2, mb: 1, borderRadius: `${theme.radius.md}px`, overflow: 'hidden', border: `1px solid ${theme.surfaces.border}` }}>
            <ReactPlayer url={videoUrl} controls width="100%" />
          </Box>
        )}

        {selectedGame && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ color: theme.text.primary, fontSize: 14, fontWeight: 700, mb: 1 }}>
              {isLiveGame ? t('gameCenterEstadisticas.periodStartTimeLive') : t('gameCenterEstadisticas.periodTime')}
            </Typography>
            {isLiveGame && (
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  mb: 2,
                  bgcolor: theme.withAlpha(theme.actionColors.info, 0.1),
                  border: `1px solid ${theme.withAlpha(theme.actionColors.info, 0.35)}`,
                  color: theme.text.primary,
                  '& .MuiAlert-icon': { color: theme.actionColors.info },
                }}
              >
                {t('gameCenterEstadisticas.periodStartTimeHelp')}
              </Alert>
            )}
            <Stack spacing={2}>
              {periodFields.map((val, idx) => (
                <CanteraTextField
                  key={idx}
                  label={`${t('gameCenterEstadisticas.period')} ${idx + 1}`} fullWidth
                  value={val}
                  onChange={(e) => {
                    const newFields = [...periodFields];
                    newFields[idx] = e.target.value;
                    setPeriodFields(newFields);
                  }}
                  placeholder={isLiveGame ? t('gameCenterEstadisticas.periodPlaceholderLive') : t('gameCenterEstadisticas.periodPlaceholder')}
                  helperText={isLiveGame ? t('gameCenterEstadisticas.periodHelperText', { period: idx + 1 }) : null}
                />
              ))}
            </Stack>
          </Box>
        )}
      </CanteraDialog>

      {/* Delete confirmation */}
      <CanteraDialog
        open={openDeleteDialog}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        title={t('gameCenterEstadisticas.confirmDelete')}
        actions={
          <>
            <CanteraButton variant="ghost" onClick={closeDeleteDialog}>{t('gameCenterEstadisticas.cancel')}</CanteraButton>
            <CanteraButton variant="danger" onClick={handleDeleteGame} startIcon={<DeleteIcon fontSize="small" />}>
              {t('gameCenterEstadisticas.delete')}
            </CanteraButton>
          </>
        }
      >
        <Typography sx={{ color: theme.text.primary, fontSize: 14 }}>
          {t('gameCenterEstadisticas.deleteConfirmMessage')}
        </Typography>
        {gameToDelete && (
          <Typography sx={{ color: theme.accent, fontSize: 14, fontWeight: 700, mt: 1.5 }}>
            {gameToDelete.game_label} vs {gameToDelete.adversary_name}
          </Typography>
        )}
        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            mt: 2,
            bgcolor: theme.withAlpha(theme.actionColors.warning, 0.1),
            border: `1px solid ${theme.withAlpha(theme.actionColors.warning, 0.35)}`,
            color: theme.text.primary,
            '& .MuiAlert-icon': { color: theme.actionColors.warning },
          }}
        >
          {t('gameCenterEstadisticas.deleteWarning')}
        </Alert>
      </CanteraDialog>
    </Box>
  );
};

export default GameCenterEstadisticas;
